import { trimDriverName, trimDriverNameFromSpreadsheet } from './utils';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import type { UUID } from '@cobliteam/common-types';

import { type Driver, type DriverSpreadsheetResult, GraphQLDriversRequester, type IDriverSpreadsheetRowAPI, type NewDriver } from '@cobliteam/api-lib';

import Alert from '@cobliteam/ui/components/Alert';

import useUser from '../useUser';

const QUERY_KEY = 'DRIVER';

const graphqlDriverRequester = new GraphQLDriversRequester();

type GetDriversParams = { options?: Partial<UseQueryOptions<Driver[]>> } | undefined;

const useDriver = () => {
  const { getSession } = useUser();
  const fleetId = getSession().fleet.id;
  const queryClient = useQueryClient();

  const getDrivers = (params?: GetDriversParams) => {
    return useQuery<Driver[]>({
      ...params?.options,
      queryKey: [QUERY_KEY, fleetId],
      queryFn: () => graphqlDriverRequester.getDrivers(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const useSaveDriver = () => {
    return useMutation<Driver, unknown, Driver>({
      mutationFn: async (driver) => graphqlDriverRequester.saveDriver(trimDriverName(driver)),
      onMutate: (driver) => {
        const previousDrivers = queryClient.getQueryData<Driver[]>([QUERY_KEY, fleetId]);
        queryClient.setQueryData<Driver[]>([QUERY_KEY, fleetId], (oldDrivers) => {
          if (oldDrivers) {
            return oldDrivers.map((d) => (d.id === driver.id ? driver : d));
          }
        });
        return { previousDrivers };
      },
      onSuccess: () => Alert.success('Motorista salvo com sucesso.'),
      onError: (err: any, variables, context: any) => {
        queryClient.setQueryData([QUERY_KEY, fleetId], context.previousDrivers);
        Alert.error('Falha ao salvar motorista. Tente novamente mais tarde.');
      },
    });
  };

  const useSaveDrivers = () => {
    return useMutation<Driver[], unknown, NewDriver[]>({
      mutationFn: async (drivers) => graphqlDriverRequester.saveDrivers(drivers.map((d) => trimDriverName(d))),
      onSuccess: () => Alert.success('Motoristas salvos com sucesso.'),
      onError: () => Alert.error('Falha ao salvar motoristas. Tente novamente mais tarde.'),
    });
  };

  const useDriversImport = () => {
    return useMutation<DriverSpreadsheetResult, unknown, IDriverSpreadsheetRowAPI[]>({
      mutationFn: async (drivers) => graphqlDriverRequester.driversImport(drivers.map((d) => trimDriverNameFromSpreadsheet(d))),
      onSuccess: () => Alert.success('Motoristas importados com sucesso.'),
      onError: () => Alert.error('Falha ao Importar motoristas. Tente novamente mais tarde.'),
    });
  };

  const useDeleteDriver = () => {
    return useMutation<UUID, unknown, UUID>({
      mutationFn: async (id) => graphqlDriverRequester.deleteDriver(id),
      onSuccess: () => {
        Alert.success('Motorista deletado com sucesso.');
        queryClient.resetQueries({ queryKey: [QUERY_KEY] });
      },
      onError: () => Alert.error('Falha ao deletar motorista. Tente novamente mais tarde.'),
    });
  };

  return {
    getDrivers,
    useSaveDriver,
    useSaveDrivers,
    useDriversImport,
    useDeleteDriver,
  };
};

export default useDriver;
